// Brand Applications page component for InfluenceHub MVP
// Shows all influencer applications received by the current brand
// Allows brands to review, approve, reject, and manage applications

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import Navbar from '../../components/layout/Navbar';
import Sidebar from '../../components/layout/Sidebar';
import { 
  APPLICATION_STATUSES, 
  BRAND_UPDATEABLE_STATUSES,
  normalizeApplicationStatus,
  getStatusBadgeConfig 
} from '../lib/applicationStatus.js';

const BrandApplications = ({ onNavigate }) => {
  const { user, profile } = useAuth();
  const [applications, setApplications] = useState([]);
  const [influencerProfiles, setInfluencerProfiles] = useState({}); // Map of influencerId -> profile data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending_review, accepted, rejected
  const [filterCampaign, setFilterCampaign] = useState('all');
  
  // Helper to get initials from name
  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };
  
  // Helper to get profile picture URL from influencer data
  const getProfilePictureUrl = (influencerData) => {
    if (!influencerData) return null;
    return (
      influencerData?.profile?.media?.profilePictureUrl ||
      influencerData?.profilePictureUrl ||
      influencerData?.avatarUrl ||
      influencerData?.photoURL ||
      null
    );
  };

  // Fetch applications for current brand's campaigns
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        console.log('🔄 Fetching applications for brand:', user?.uid);
        
        if (!user || !profile) {
          console.log('❌ No user or profile found');
          setLoading(false);
          return;
        }

        if (!db) {
          throw new Error('Firebase database not initialized');
        }

        // First, get all campaigns for this brand
        const campaignsQuery = query(
          collection(db, 'campaigns'),
          where('brandId', '==', user.uid)
        );
        
        const campaignsSnapshot = await getDocs(campaignsQuery);
        const campaignIds = campaignsSnapshot.docs.map(doc => doc.id);
        
        console.log('📊 Found campaigns:', campaignIds.length);
        
        if (campaignIds.length === 0) {
          setApplications([]);
          setLoading(false);
          return;
        }

        // Query applications by brandId (preferred method)
        let allApplications = [];
        try {
          const brandIdQuery = query(
            collection(db, 'applications'),
            where('brandId', '==', user.uid)
          );
          const brandIdSnapshot = await getDocs(brandIdQuery);
          const brandIdApps = brandIdSnapshot.docs.map(d => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate() || new Date(),
            updatedAt: d.data().updatedAt?.toDate() || new Date()
          }));
          allApplications = [...allApplications, ...brandIdApps];
          console.log('📊 Found applications via brandId:', brandIdApps.length);
        } catch (brandIdError) {
          console.warn('⚠️ brandId query failed, trying fallback:', brandIdError);
        }
        
        // Fallback: query by campaignId (for older applications without brandId)
        if (campaignIds.length > 0) {
          // Split into chunks of 10 to handle Firestore 'in' query limit
          for (let i = 0; i < campaignIds.length; i += 10) {
            const chunk = campaignIds.slice(i, i + 10);
            try {
              const campaignIdQuery = query(
                collection(db, 'applications'),
                where('campaignId', 'in', chunk)
              );
              const campaignIdSnapshot = await getDocs(campaignIdQuery);
              const campaignIdApps = campaignIdSnapshot.docs
                .map(d => ({
                  id: d.id,
                  ...d.data(),
                  createdAt: d.data().createdAt?.toDate() || new Date(),
                  updatedAt: d.data().updatedAt?.toDate() || new Date()
                }))
                // Filter out duplicates (already found via brandId)
                .filter(app => !allApplications.find(a => a.id === app.id));
              allApplications = [...allApplications, ...campaignIdApps];
              console.log(`📊 Found additional applications via campaignId (chunk ${i/10 + 1}):`, campaignIdApps.length);
            } catch (chunkError) {
              console.warn('⚠️ Error querying campaign chunk:', chunkError);
            }
          }
        }
        
        // Filter to only show applications that belong to this brand
        const filteredApplications = allApplications.filter(app => {
          const appBrandId = app.brandId || null;
          return appBrandId === user.uid;
        });
        
        // Sort by submittedAt descending
        filteredApplications.sort((a, b) => {
          const aDate = a.submittedAt?.toDate?.() || a.createdAt || new Date(0);
          const bDate = b.submittedAt?.toDate?.() || b.createdAt || new Date(0);
          return bDate - aDate;
        });
        
        console.log('📊 Total applications found:', filteredApplications.length);
        setApplications(filteredApplications);
        
        // Fetch influencer profiles for profile pictures
        const uniqueInfluencerIds = [...new Set(filteredApplications.map(app => app.influencerId).filter(Boolean))];
        console.log('👥 Fetching profiles for', uniqueInfluencerIds.length, 'influencers...');
        
        const profilesMap = {};
        await Promise.all(
          uniqueInfluencerIds.map(async (influencerId) => {
            try {
              const influencerRef = doc(db, 'users', influencerId);
              const influencerSnap = await getDoc(influencerRef);
              if (influencerSnap.exists()) {
                profilesMap[influencerId] = influencerSnap.data();
              }
            } catch (err) {
              console.warn(`⚠️ Could not fetch profile for influencer ${influencerId}:`, err);
            }
          })
        );
        
        console.log('✅ Fetched', Object.keys(profilesMap).length, 'influencer profiles');
        setInfluencerProfiles(profilesMap);
      } catch (error) {
        console.error('❌ Error fetching applications:', error);
        setError(`Failed to load applications: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [user, profile]);

  // Update application status
  const updateApplicationStatus = async (applicationId, newStatus) => {
    try {
      // Normalize and validate status
      const normalizedStatus = normalizeApplicationStatus(newStatus);
      
      // Only allow accepted or rejected for brand updates
      if (!BRAND_UPDATEABLE_STATUSES.includes(normalizedStatus)) {
        alert('Invalid status. Only "Accepted" or "Rejected" can be set.');
        return;
      }
      
      console.log(`🔄 Updating application ${applicationId} to ${normalizedStatus}...`);
      setUpdating(applicationId);
      
      if (!db) {
        throw new Error('Firebase database not initialized');
      }

      const applicationRef = doc(db, 'applications', applicationId);
      await updateDoc(applicationRef, {
        status: normalizedStatus,
        updatedAt: serverTimestamp(),
        reviewedAt: serverTimestamp()
      });

      // Update local state
      setApplications(prev => 
        prev.map(app => 
          app.id === applicationId 
            ? { ...app, status: normalizedStatus, updatedAt: new Date() }
            : app
        )
      );

      const config = getStatusBadgeConfig(normalizedStatus);
      console.log(`✅ Application ${normalizedStatus} successfully`);
      alert(`Application ${config.label} successfully`);
    } catch (error) {
      console.error(`❌ Error updating application:`, error);
      setError(`Failed to update application: ${error.message}`);
    } finally {
      setUpdating(null);
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const normalized = normalizeApplicationStatus(status);
    const config = getStatusBadgeConfig(normalized);
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.icon} {config.label}
      </span>
    );
  };

  // Get filtered applications
  const getFilteredApplications = () => {
    let filtered = applications;

    // Filter by status
    if (filterStatus !== 'all') {
      const normalizedFilter = normalizeApplicationStatus(filterStatus);
      filtered = filtered.filter(app => normalizeApplicationStatus(app.status) === normalizedFilter);
    }

    // Filter by campaign (if we had campaign names)
    if (filterCampaign !== 'all') {
      // This would filter by campaign name if we had that data
      // For now, we'll skip this filter
    }

    return filtered;
  };

  // Get status counts
  const getStatusCount = (status) => {
    // Normalize status for comparison
    const normalizedStatus = normalizeApplicationStatus(status);
    return applications.filter(app => normalizeApplicationStatus(app.status) === normalizedStatus).length;
  };

  // Get unique campaigns from applications
  const getUniqueCampaigns = () => {
    const campaigns = [...new Set(applications.map(app => app.campaignId))];
    return campaigns;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading applications...</p>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-red-600 text-6xl mb-4">⚠️</div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load Applications</h1>
                  <p className="text-gray-600 mb-6">{error}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const filteredApplications = getFilteredApplications();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation bar */}
      <Navbar onNavigate={onNavigate} />
      
      <div className="flex">
        {/* Sidebar navigation */}
        <Sidebar onNavigate={onNavigate} />
        
        {/* Main content area */}
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            {/* Page header */}
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900">Applications</h1>
              <p className="mt-2 text-gray-600">
                Review and manage influencer applications for your campaigns
              </p>
            </div>

            {/* Application Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Applications</h3>
                <p className="text-3xl font-bold text-blue-600">{applications.length}</p>
                <p className="text-sm text-gray-500">All time</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending Review</h3>
                <p className="text-3xl font-bold text-yellow-600">{getStatusCount(APPLICATION_STATUSES.PENDING_REVIEW)}</p>
                <p className="text-sm text-gray-500">Awaiting decision</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Accepted</h3>
                <p className="text-3xl font-bold text-green-600">{getStatusCount(APPLICATION_STATUSES.ACCEPTED)}</p>
                <p className="text-sm text-gray-500">Accepted applications</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Rejected</h3>
                <p className="text-3xl font-bold text-red-600">{getStatusCount(APPLICATION_STATUSES.REJECTED)}</p>
                <p className="text-sm text-gray-500">Declined applications</p>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex flex-wrap gap-4">
                <div>
                  <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Status
                  </label>
                  <select
                    id="status-filter"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value={APPLICATION_STATUSES.PENDING_REVIEW}>Pending Review</option>
                    <option value={APPLICATION_STATUSES.ACCEPTED}>Accepted</option>
                    <option value={APPLICATION_STATUSES.REJECTED}>Rejected</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="campaign-filter" className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Campaign
                  </label>
                  <select
                    id="campaign-filter"
                    value={filterCampaign}
                    onChange={(e) => setFilterCampaign(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Campaigns</option>
                    {/* TODO: Add campaign options when we have campaign names */}
                  </select>
                </div>
              </div>
            </div>

            {/* Applications List */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Applications ({filteredApplications.length})
                  </h2>
                  <button
                    onClick={() => window.location.reload()}
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    Refresh
                  </button>
                </div>
              </div>
              
              {filteredApplications.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-gray-400 text-6xl mb-4">📝</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {applications.length === 0 ? 'No applications yet' : 'No applications match your filters'}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {applications.length === 0 
                      ? 'Applications from influencers will appear here once they start applying to your campaigns.'
                      : 'Try adjusting your filters to see more applications.'
                    }
                  </p>
                  {applications.length === 0 && (
                    <button
                      onClick={() => onNavigate('create-campaign')}
                      className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      Create Your First Campaign
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-6 font-medium text-gray-600">Influencer</th>
                        <th className="text-left p-6 font-medium text-gray-600">Campaign</th>
                        <th className="text-left p-6 font-medium text-gray-600">Status</th>
                        <th className="text-left p-6 font-medium text-gray-600">Applied</th>
                        <th className="text-left p-6 font-medium text-gray-600">Message</th>
                        <th className="text-right p-6 font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredApplications.map((application) => (
                        <tr
                          key={application.id}
                          className="border-b hover:bg-gray-50 cursor-pointer"
                          onClick={() => onNavigate('application-detail', application.id)}
                        >
                          <td className="p-6">
                            <div className="flex items-center gap-3">
                              {/* Profile picture */}
                              {(() => {
                                const influencerData = influencerProfiles[application.influencerId];
                                const profilePictureUrl = getProfilePictureUrl(influencerData);
                                const influencerName = application.influencerName || 
                                  (influencerData ? `${influencerData.firstName || ''} ${influencerData.lastName || ''}`.trim() : '') ||
                                  'Unknown Influencer';
                                const initials = getInitials(influencerName);
                                
                                return (
                                  <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center bg-gray-200 flex-shrink-0 relative">
                                    {profilePictureUrl ? (
                                      <img 
                                        src={profilePictureUrl} 
                                        alt={influencerName}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          if (e.target) {
                                            e.target.style.display = 'none';
                                            const fallback = e.target.parentElement?.querySelector('.avatar-fallback');
                                            if (fallback) fallback.style.display = 'flex';
                                          }
                                        }}
                                      />
                                    ) : null}
                                    <div className={`avatar-fallback absolute inset-0 flex items-center justify-center ${profilePictureUrl ? 'hidden' : ''}`}>
                                      <span className="text-xs font-medium text-gray-600">{initials}</span>
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Name and email */}
                              <div>
                                <p className="font-medium text-gray-900 hover:text-blue-600">
                                  {application.influencerName || 
                                   (influencerProfiles[application.influencerId] 
                                     ? `${influencerProfiles[application.influencerId].firstName || ''} ${influencerProfiles[application.influencerId].lastName || ''}`.trim()
                                     : 'Unknown Influencer')}
                                </p>
                                <p className="text-sm text-gray-500">{application.influencerEmail || 'No email'}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-6">
                            <p className="font-medium text-gray-900 hover:text-blue-600">
                              {application.campaignTitle || application.campaignName || 'Unknown Campaign'}
                            </p>
                            <p className="text-sm text-gray-500">ID: {application.campaignId}</p>
                          </td>
                          <td className="p-6">
                            {getStatusBadge(application.status)}
                          </td>
                          <td className="p-6 text-sm text-gray-500">
                            {formatDate(application.createdAt)}
                          </td>
                          <td className="p-6">
                            <p className="text-sm text-gray-700 max-w-xs truncate">
                              {application.message || 'No message provided'}
                            </p>
                          </td>
                          <td className="p-6 text-right">
                            <div className="flex justify-end gap-2">
                              {normalizeApplicationStatus(application.status) === APPLICATION_STATUSES.PENDING_REVIEW && (
                                <>
                                  <button
                                    className="px-3 py-1.5 text-sm font-medium text-green-600 bg-white border border-gray-300 rounded-md hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => updateApplicationStatus(application.id, APPLICATION_STATUSES.ACCEPTED)}
                                    disabled={updating === application.id}
                                  >
                                    {updating === application.id ? (
                                      <div className="h-4 w-4 animate-spin border-2 border-green-600 border-t-transparent rounded-full"></div>
                                    ) : (
                                      '✅ Accept'
                                    )}
                                  </button>
                                  <button
                                    className="px-3 py-1.5 text-sm font-medium text-red-600 bg-white border border-gray-300 rounded-md hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => updateApplicationStatus(application.id, APPLICATION_STATUSES.REJECTED)}
                                    disabled={updating === application.id}
                                  >
                                    {updating === application.id ? (
                                      <div className="h-4 w-4 animate-spin border-2 border-red-600 border-t-transparent rounded-full"></div>
                                    ) : (
                                      '❌ Reject'
                                    )}
                                  </button>
                                </>
                              )}
                              {normalizeApplicationStatus(application.status) !== APPLICATION_STATUSES.PENDING_REVIEW && (
                                <span className="text-sm text-gray-500">
                                  Reviewed {formatDate(application.updatedAt)}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default BrandApplications;
